import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, PlugZap, Save } from 'lucide-react';
import * as colaApi from '@/api/cola.api';
import { listChannels } from '@/api/channels.api';
import { Card, CardBody, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Toggle } from '@/components/ui/Toggle';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { PageHeader } from '@/components/common/PageHeader';
import { useToast } from '@/hooks/useToast';

/**
 * Cola de envío (`relay_cola`, F5.6, E-012).
 *
 * Un relevo NO PIENSA: toma lo que otro sistema dejó en su cola, lo manda por
 * el canal de WhatsApp que se elige acá y reporta cómo le fue. Lo que la gente
 * le escribe a ese número se le entrega a ese sistema.
 *
 * El candado de simulacro del canal NO se toca desde esta pantalla: se quita
 * en Canales, con su confirmación. Acá solo se avisa si está puesto.
 */

const cuando = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString('es-DO', { dateStyle: 'short', timeStyle: 'medium' }) : '—';

function EstadoCard({ vista }: { vista: colaApi.VistaConfigRelay }) {
  const estado = useQuery({
    queryKey: ['cola', 'estado'],
    queryFn: colaApi.getEstado,
    refetchInterval: 10_000,
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Cómo va</CardTitle>
        <CardDescription>Se actualiza solo cada 10 segundos. Se reinicia si se reinicia el servidor.</CardDescription>
      </CardHeader>
      <CardBody className="space-y-3 text-sm">
        <div className="flex flex-wrap items-center gap-2">
          {vista.activo ? <Badge variant="brand">Encendido</Badge> : <Badge variant="default">Apagado</Badge>}
          {vista.canal ? (
            <span className="text-slate-600 dark:text-slate-300">
              Canal <strong>{vista.canal.name}</strong> · {vista.canal.status}
            </span>
          ) : vista.configurado ? (
            <span className="text-danger">El canal configurado ya no existe.</span>
          ) : null}
        </div>

        {vista.canal?.simulacro && (
          <p className="flex items-start gap-2 text-amber-600 dark:text-amber-400">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            El canal está en simulacro: nada sale a un teléfono y cada envío se reporta como no enviado.
            El candado se quita en Canales, no acá.
          </p>
        )}

        {estado.isLoading ? (
          <Spinner />
        ) : estado.isError ? (
          <p className="text-danger">No se pudo leer el estado del relevo.</p>
        ) : estado.data ? (
          <dl className="grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-4">
            <div>
              <dt className="text-xs text-slate-400">Última vuelta</dt>
              <dd>{cuando(estado.data.ultimaVuelta)}</dd>
            </div>
            <div>
              <dt className="text-xs text-slate-400">Enviados</dt>
              <dd>{estado.data.totalEnviados}</dd>
            </div>
            <div>
              <dt className="text-xs text-slate-400">No enviados</dt>
              <dd>{estado.data.totalFallidos}</dd>
            </div>
            <div>
              <dt className="text-xs text-slate-400">Frenados por calentamiento</dt>
              <dd>{estado.data.ultimoResumen?.frenados ?? 0}</dd>
            </div>
            {estado.data.ultimoResumen?.espera && (
              <div className="col-span-full text-slate-500">{estado.data.ultimoResumen.espera}</div>
            )}
            {estado.data.ultimoError && (
              <div className="col-span-full text-danger">Último error: {estado.data.ultimoError}</div>
            )}
          </dl>
        ) : null}
      </CardBody>
    </Card>
  );
}

function ConexionCard({ vista }: { vista: colaApi.VistaConfigRelay }) {
  const qc = useQueryClient();
  const toast = useToast();
  const canales = useQuery({ queryKey: ['cola', 'canales'], queryFn: listChannels });

  const [activo, setActivo] = useState(false);
  const [url, setUrl] = useState('');
  const [token, setToken] = useState('');
  const [channelId, setChannelId] = useState('');
  const [intervalo, setIntervalo] = useState('15');

  useEffect(() => {
    setActivo(vista.activo);
    setUrl(vista.url ?? '');
    setChannelId(vista.channelId ?? '');
    setIntervalo(String(vista.intervaloSegundos));
  }, [vista]);

  const guardar = useMutation({
    mutationFn: () =>
      colaApi.putConfig({
        activo,
        url,
        token: token.trim() || null,
        channelId,
        intervaloSegundos: Number(intervalo),
      }),
    onSuccess: () => {
      setToken('');
      void qc.invalidateQueries({ queryKey: ['cola'] });
      toast.success('Relevo guardado');
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : 'No se pudo guardar el relevo'),
  });

  const probar = useMutation({
    mutationFn: colaApi.probarConexion,
    onSuccess: (r) => (r.ok ? toast.success(r.mensaje) : toast.error(r.mensaje)),
    onError: (err) => toast.error(err instanceof Error ? err.message : 'No se pudo probar la conexión'),
  });

  const segundos = Number(intervalo);
  const valido =
    url.trim().length > 0 &&
    channelId.length > 0 &&
    Number.isInteger(segundos) &&
    segundos >= 5 &&
    segundos <= 300 &&
    (vista.tokenMascara !== null || token.trim().length >= 16);

  const opcionesDeCanal = [
    { value: '', label: canales.isLoading ? 'Cargando canales…' : 'Elegí el canal que manda' },
    ...(canales.data ?? []).map((c) => ({ value: c.id, label: `${c.name} · ${c.status}` })),
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Conexión con el sistema de la cola</CardTitle>
        <CardDescription>
          La dirección y el token los da el sistema dueño de la cola. El token se guarda cifrado y no se vuelve a
          mostrar.
        </CardDescription>
      </CardHeader>
      <CardBody className="space-y-4">
        <Toggle checked={activo} onChange={setActivo} label="Relevo encendido" />

        <Input
          label="Dirección del sistema de la cola"
          placeholder="https://…"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          hint="Solo la dirección base. Fuera de esta máquina tiene que ser https://."
        />

        <Input
          label="Token del relevo"
          type="password"
          autoComplete="off"
          placeholder={vista.tokenMascara ?? 'Mínimo 16 caracteres'}
          value={token}
          onChange={(e) => setToken(e.target.value)}
          hint={
            vista.tokenMascara
              ? `Guardado (${vista.tokenMascara}). Dejalo vacío para conservarlo.`
              : 'El mismo valor que tiene configurado el sistema de la cola.'
          }
        />

        {canales.isError ? (
          <p className="text-sm text-danger">No se pudieron cargar los canales.</p>
        ) : (
          <Select
            label="Canal que manda"
            value={channelId}
            onChange={(e) => setChannelId(e.target.value)}
            options={opcionesDeCanal}
          />
        )}

        <Input
          label="Cada cuántos segundos revisa la cola"
          type="number"
          min="5"
          max="300"
          value={intervalo}
          onChange={(e) => setIntervalo(e.target.value)}
        />

        <div className="flex flex-wrap gap-2">
          <Button onClick={() => guardar.mutate()} loading={guardar.isPending} disabled={!valido}>
            <Save className="h-4 w-4" /> Guardar relevo
          </Button>
          <Button
            onClick={() => probar.mutate()}
            loading={probar.isPending}
            disabled={!vista.configurado}
            title={vista.configurado ? undefined : 'Primero guardá la configuración'}
          >
            <PlugZap className="h-4 w-4" /> Probar conexión
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}

export default function Cola() {
  const config = useQuery({ queryKey: ['cola', 'config'], queryFn: colaApi.getConfig });

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title="Cola de envío"
        description="Toma lo que otro sistema dejó en su cola, lo manda por WhatsApp y reporta. Sin IA."
      />
      {config.isLoading ? (
        <Spinner />
      ) : config.isError || !config.data ? (
        <Card>
          <CardBody>
            <p className="text-sm text-danger">
              No se pudo leer la configuración del relevo.{' '}
              {config.error instanceof Error ? config.error.message : ''}
            </p>
          </CardBody>
        </Card>
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          <ConexionCard vista={config.data} />
          <EstadoCard vista={config.data} />
        </div>
      )}
    </div>
  );
}
